import { Records } from "@buildwithflux/shared";
import { sumBy } from "lodash";
import { vec2 } from "../common/geometry/vec2";
import { SymbolLayout } from "../elements";
import { DEFAULT_SYMBOL_NAME, ElementHelper } from "../elements/ElementHelper";
import { setElementsAndGenerateNodes } from "../nodes/setElementsAndGenerateNodes";
import { isTerminalEqual } from "./helpers";
import { WireProjectionSmart } from "./WireProjectionSmart";
import { Wiring } from "./Wiring";
/** Cost threshold for placing a portal.  This is in "units" of wire length */
const PORTAL_STRATEGY_COST = 100;
/** Spacing between element terminals and new portal/ground terminals */
const ELEMENT_TO_PORTAL_DISTANCE = 10;
/** Return the provided list as pairs of [current, next]. The last element is not included in the
 * iteration (it is included as the `next` of the second-to-last element), so that both `current`
 * and `next` are always defined.
 */
function pairs(array) {
    return array.slice(0, -1).map((v, idx) => [v, array[idx + 1]]);
}
function totalWireLength(projectedWire) {
    return sumBy(pairs(projectedWire), (w) => vec2.distance(w[0].position, w[1].position));
}
/**
 * Generate a new net name for connecting two terminals together using portals.
 */
function createNetNameForConnectingPortals(document, startPin, endPin) {
    const startElement = document.elements[startPin.element_uid];
    const targetElement = document.elements[endPin.element_uid];
    const startTerminal = startElement?.part_version_data_cache.terminals[startPin.terminal_uid];
    const targetTerminal = targetElement?.part_version_data_cache.terminals[endPin.terminal_uid];
    const startElementName = startElement?.label ?? "SRC";
    const startTerminalName = startTerminal?.name ?? "SRC PIN";
    const targetElementName = targetElement?.label ?? "TGT";
    const targetTerminalName = targetTerminal?.name ?? "TGT PIN";
    return `${startElementName} ${startTerminalName} - ${targetElementName} ${targetTerminalName}`;
}
export function getFirstTerminal(element) {
    return {
        element_uid: element.uid,
        terminal_uid: Records.firstValue(element.part_version_data_cache.terminals).uid,
    };
}
function calculatePortalPartPlacement(wiring, portalElement, terminal) {
    let portalPinPosition = Records.firstValue(SymbolLayout.getSymbolInfo(portalElement)[0].pinLocations)?.position;
    if (!portalPinPosition || !ElementHelper.isPortalOrGroundElement(portalElement)) {
        return { x: 0, y: 0, orientation: 0 };
    }
    const derivedElementData = wiring.getDerivedElementData(terminal.element_uid);
    const terminalDirection = derivedElementData?.absolutePinLocations[terminal.terminal_uid]?.direction;
    const terminalSite = wiring.getWireConnectionSite({ terminal });
    let portalOrientation;
    const rotations = { left: 0, down: Math.PI / 2, right: Math.PI, up: (3 * Math.PI) / 2 };
    if (ElementHelper.isPortal(portalElement)) {
        portalOrientation = rotations[terminalDirection ?? "left"];
    }
    else {
        portalOrientation = terminalDirection === "up" ? Math.PI : 0;
    }
    const distanceFromTerminal = vec2.rotate({ x: -ELEMENT_TO_PORTAL_DISTANCE, y: 0 }, rotations[terminalDirection ?? "left"]);
    portalPinPosition = vec2.rotate(portalPinPosition, portalOrientation);
    return {
        ...vec2.chain(terminalSite.position).add(distanceFromTerminal).sub(portalPinPosition).xy(),
        orientation: portalOrientation,
    };
}
/**
 * Finds an existing portal (or ground) that is connected to the given terminal, if one exists.
 */
function findEligibleExistingPortalForTerminal(document, pin) {
    const connectedPins = ElementHelper.getDirectlyConnectedPins(pin, document);
    // Also consider the pin's element itself as a potential portal, so we don't
    // create extra portals if copilot asks as to connect a portal to something.
    connectedPins.push(pin);
    return connectedPins
        .map((pin) => document.elements[pin.element_uid])
        .find((el) => el && ElementHelper.isPortalOrGroundElement(el));
}
/**
 * Merge two portals into one.
 *
 * MUTATES DOCUMENT DATA!
 */
function mergePortals(document, portalA, portalB) {
    // If they already have the same label, by the semantics of portals they're already connected.
    // TODO: shouldn't we be able to do this with nets?
    if (portalA.label === portalB.label)
        return;
    // If the source portal label is null, and the target portal label isn't, inherit it.
    if (portalA.label == null && portalB.label != null) {
        portalA.label = portalB.label;
        return;
    }
    // If the target portal label is null, and the source portal label isn't, inherit it.
    if (portalB.label == null && portalA.label != null) {
        portalB.label = portalA.label;
        return;
    }
    // Ok, so neither are null, and they're not the same. We need to merge them.
    const sourcePortalMatches = Object.values(document.elements).filter((el) => el.label === portalA.label);
    const targetPortalMatches = Object.values(document.elements).filter((el) => el.label === portalB.label);
    // If the source portal dominates, choose it.
    if (sourcePortalMatches.length > targetPortalMatches.length) {
        for (const targetPortalMatch of targetPortalMatches) {
            targetPortalMatch.label = portalA.label;
        }
    }
    // If the target portal dominates, choose it.
    else {
        for (const sourcePortalMatch of sourcePortalMatches) {
            sourcePortalMatch.label = portalB.label;
        }
    }
}
/**
 * Creates a new portal or ground to "match" a given terminal.
 */
export function connectPortalNearPin(document, portalName, pin, isGroundNet, actionContext) {
    let wiring = new Wiring(document.routeVertices, document.elements);
    const newPortal = ElementHelper.createNewElementData(isGroundNet ? actionContext.groundPart : actionContext.portalPart, { x: 0, y: 0 }, actionContext.userUid, portalName);
    const placement = calculatePortalPartPlacement(wiring, newPortal, pin);
    newPortal.symbol_placements[DEFAULT_SYMBOL_NAME] = placement;
    setElementsAndGenerateNodes(document, [newPortal]);
    // Wire from the terminal to the portal. Need to construct a new `wiring` instance because we've
    // added an element in the meantime.
    // TODO we def need to make that more ergonomic, so we can update a live `wiring` instance
    wiring = new Wiring(document.routeVertices, document.elements);
    wiring.placeWire({ terminal: pin }, { terminal: getFirstTerminal(newPortal) });
    wiring.applyChangesToDocument(document);
    return newPortal;
}
/**
 * This module defines an auto-wiring strategy that will connect two terminals together
 * using portals.
 *
 * NOTE: THIS FUNCTION WILL MUTATE THE DOCUMENT DATA.
 */
function portalAutoWireStrategy(document, connection, isGroundNet, actionContext) {
    const sourcePortal = findEligibleExistingPortalForTerminal(document, connection.start);
    const targetPortal = findEligibleExistingPortalForTerminal(document, connection.end);
    if (sourcePortal && targetPortal) {
        /**
         * In this first case, both source and target are ALREADY connected to portals.  This means we do the portal merging dance
         * in which we assign them to a common portal.  Which portal?  We basically pick the portal that corresponds to the net with "more stuff".
         * Unfortunately we can't use the same net merging semantics here, because source and target are completely arbitrary in this case.
         */
        mergePortals(document, sourcePortal, targetPortal);
    }
    else if (sourcePortal || targetPortal) {
        const portal = sourcePortal ?? targetPortal;
        const unconnectedPin = sourcePortal ? connection.end : connection.start;
        /**
         * Here one portal exists, but the other does not.  This means that we want to reuse the existing
         * portal, and create a new portal for other end.  The new name for the portal will be inherited from the source portal,
         * unless it doesn't exist, in which case we'll create a new name.
         */
        const portalName = portal.label ?? createNetNameForConnectingPortals(document, connection.start, connection.end);
        connectPortalNearPin(document, portalName, unconnectedPin, isGroundNet, actionContext);
    }
    else {
        // No portals exist at all, create both
        const portalName = createNetNameForConnectingPortals(document, connection.start, connection.end);
        connectPortalNearPin(document, portalName, connection.start, isGroundNet, actionContext);
        connectPortalNearPin(document, portalName, connection.end, isGroundNet, actionContext);
    }
}
/**
 * This module defines an auto-wiring strategy that will connect an arbitrary number of terminals together by considering an overall "configuration cost".
 *
 * NOTE: THIS FUNCTION WILL MUTATE THE DOCUMENT DATA.
 */
export function costBasedAutoWire(document, 
/** The list of all connections to be made. */
connectionsToWire, isGroundNet, actionContext) {
    const pointToPointWireConnections = [];
    for (const connection of connectionsToWire) {
        // New Wiring instance each iteration, because the document may change between iterations
        const wiring = new Wiring(document.routeVertices, document.elements);
        const source = wiring.getWireConnectionSite({ terminal: connection.start });
        const target = wiring.getWireConnectionSite({ terminal: connection.end });
        const wiringAlgo = new WireProjectionSmart(wiring);
        const projectedWire = wiringAlgo.project(source, target);
        const startVertex = wiring.findVertex((v) => v.terminal && isTerminalEqual(v.terminal, connection.start));
        if (startVertex) {
            const pinsConnectedToStart = wiring
                .getAllNetVerticesAcrossPortals(startVertex.id)
                .map((v) => wiring.vertices.get(v)?.terminal)
                .filter((t) => !!t);
            // Don't create wires or portals for pins that are already connected to the same net.
            const alreadyConnected = pinsConnectedToStart.some((p) => isTerminalEqual(p, connection.end));
            if (alreadyConnected)
                continue;
        }
        const wireCost = totalWireLength(projectedWire);
        if (wireCost <= PORTAL_STRATEGY_COST) {
            pointToPointWireConnections.push(connection);
        }
        else {
            portalAutoWireStrategy(document, connection, isGroundNet, actionContext);
        }
    }
    const wiring = new Wiring(document.routeVertices, document.elements);
    for (const connection of pointToPointWireConnections) {
        wiring.placeWire({ terminal: connection.start }, { terminal: connection.end });
    }
    wiring.applyChangesToDocument(document);
}
//# sourceMappingURL=costBasedAutoWire.js.map